import { useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, X as XIcon } from "@phosphor-icons/react";

/**
 * Modal to pick one or more existing serial numbers from the available
 * list returned by `GET /serials/available`. Supports search filtering and
 * select-all / deselect-all of the currently filtered subset.
 */
export default function SerialPickerModal({ open, onClose, serials, filter, setFilter, onConfirm }) {
  const [selected, setSelected] = useState(new Set());

  if (!open) return null;

  const filtered = filter
    ? serials.filter((s) => s.serial_number.toLowerCase().includes(filter.toLowerCase()))
    : serials;

  const toggle = (sn) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sn)) next.delete(sn); else next.add(sn);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((s) => s.serial_number)));
    }
  };

  const confirm = () => {
    if (selected.size === 0) { toast.error("Seleziona almeno un seriale"); return; }
    onConfirm(Array.from(selected));
    setSelected(new Set());
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white border border-neutral-200 shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col" data-testid="serial-picker-modal">
        <div className="p-4 md:p-6 border-b border-neutral-200 flex items-center justify-between">
          <div>
            <div className="label-eyebrow mb-1">Seriali Disponibili</div>
            <h2 className="font-display font-bold text-lg tracking-tight">
              Seleziona dal magazzino <span className="text-neutral-500 text-sm font-normal">({serials.length} disponibili)</span>
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-neutral-100 transition-colors"><XIcon size={18} /></button>
        </div>
        <div className="p-4 md:p-6 flex-1 flex flex-col overflow-hidden">
          <div className="flex items-center gap-2 bg-neutral-50 border border-neutral-200 px-3 py-2 mb-3">
            <MagnifyingGlass size={16} className="text-neutral-400" />
            <input
              data-testid="serial-picker-filter"
              autoFocus
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filtra per seriale..."
              className="flex-1 outline-none text-sm bg-transparent font-mono"
            />
          </div>
          <div className="flex items-center justify-between mb-2 text-xs">
            <button
              type="button"
              data-testid="serial-picker-toggle-all"
              onClick={toggleAll}
              className="text-[#002FA7] hover:underline font-medium"
            >
              {selected.size === filtered.length && filtered.length > 0 ? "Deseleziona tutti" : "Seleziona tutti"}
            </button>
            <span className="text-neutral-500">{selected.size} selezionati</span>
          </div>
          <div className="flex-1 border border-neutral-200 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-8 text-center text-sm text-neutral-500">Nessun seriale corrisponde al filtro.</div>
            ) : (
              <ul>
                {filtered.map((s) => (
                  <li key={s.serial_number} className="border-b border-neutral-100 last:border-b-0">
                    <label className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${selected.has(s.serial_number) ? "bg-[#002FA7]/5" : "hover:bg-neutral-50"}`}>
                      <input
                        type="checkbox"
                        data-testid={`picker-check-${s.serial_number}`}
                        checked={selected.has(s.serial_number)}
                        onChange={() => toggle(s.serial_number)}
                        className="w-4 h-4 accent-[#002FA7]"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-sm font-semibold">{s.serial_number}</div>
                        <div className="flex items-center gap-2 flex-wrap mt-0.5">
                          {s.warehouse_name && (
                            <span className="text-[10px] font-semibold uppercase tracking-wider bg-neutral-100 text-neutral-700 px-1.5 py-0.5">
                              📍 {s.warehouse_name}
                            </span>
                          )}
                          {s.last_movement_date && (
                            <span className="text-[10px] text-neutral-500">
                              {new Date(s.last_movement_date).toLocaleString("it-IT")}
                            </span>
                          )}
                        </div>
                      </div>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="p-4 md:p-6 pt-3 flex justify-end gap-2 border-t border-neutral-200">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-neutral-300 hover:bg-neutral-100 transition-colors">Annulla</button>
          <button
            type="button"
            data-testid="serial-picker-confirm"
            onClick={confirm}
            className="px-4 py-2 text-sm bg-[#002FA7] hover:bg-[#002480] text-white transition-colors"
          >
            Aggiungi {selected.size > 0 ? `(${selected.size})` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}
